import React, { useState, useMemo } from "react";
import Select from "react-select";

export interface CheckboxPopupOption<T1, T2> {
  label: string;
  value: T1;
  subOptions?: T2[];
  subLabel?: string;
}

interface CheckboxPopupProps<T1, T2> {
  options: CheckboxPopupOption<T1, T2>[];
  onConfirm: (selected: { value: T1; subSelected?: T2[] }[]) => void;
  onCancel: () => void;
  title?: string;
}

function CheckboxPopup<T1, T2>({
  options,
  onConfirm,
  onCancel,
  title = "Select Options",
}: CheckboxPopupProps<T1, T2>) {
  const [selected, setSelected] = useState<T1[]>([]);
  const [subSelections, setSubSelections] = useState<Record<string, { label: string, value: T2 }[]>>({});

  const allValues = useMemo(() => options.map((o) => o.value), [options]);
  const allSelected = selected.length === options.length && options.length > 0;

  const toggleOption = (value: T1) => {
    setSelected((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.length === options.length ? [] : allValues
    );
  };

  const handleConfirm = () => {
    const result = selected.map((val) => {
      const key = String(val);
      const sub = (subSelections[key] || []).map(ss => ss.value);
      return { value: val, subSelected: sub?.length ? sub : undefined };
    });
    onConfirm(result);
  };

  const handleSubSelectChange = (value: T1, newValues: any) => {
    const key = String(value);
    setSubSelections((prev) => ({
      ...prev,
      [key]: newValues || [],
    }));
  };

  return (
      <div
          style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
          }}
      >
          <div
              style={{
                  backgroundColor: "var(--input-bg-color)",
                  padding: "25px",
                  borderRadius: "12px",
                  minWidth: "360px",
                  color: "var(--input-text-color)",
                  boxShadow: "0 0 20px rgba(0,0,0,0.3)",
              }}
          >
              <h3 style={{marginBottom: "15px", color: "var(--form-label-color)"}}>
                  {title}
              </h3>
              <div style={{marginBottom: "20px"}}>
              <label
                style={{
                  display: "block",
                  marginBottom: "12px",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  style={{
                    marginRight: "8px",
                    accentColor: "var(--green-button-default-color)",
                    cursor: "pointer",
                  }}
                />
                Select all
              </label>
                  {options.map((opt, idx) => {
                      const isSelected = selected.includes(opt.value);
                      const hasSub = opt.subOptions && opt.subOptions.length > 0;
                      return (
                          <div key={idx} style={{ marginBottom: "15px" }}>
                              <label
                                  style={{
                                    display: "block",
                                    marginBottom: hasSub ? "6px" : "10px",
                                    cursor: "pointer",
                                  }}
                              >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleOption(opt.value)}
                                    style={{
                                      marginRight: "8px",
                                      accentColor: "var(--green-button-default-color)",
                                      cursor: "pointer",
                                    }}
                                  />
                                    {opt.label}
                                  </label>
                                {hasSub && isSelected && (
                                  <div style={{ marginLeft: "20px" }}>
                                    <label
                                      style={{
                                        display: "block",
                                        marginBottom: "6px",
                                        color: "var(--form-label-color)",
                                      }}
                                    >
                                      {opt.subLabel ?? "Select related keywords:"}
                                    </label>
                                    <Select
                                      options={opt.subOptions?.map((s: any) => typeof s === "object" && "label" in s && "value" in s ? s : { label: String(s), value: s }) ?? []}
                                      isMulti
                                      placeholder="Type to search..."
                                      value={subSelections[String(opt.value)] || []}
                                      onChange={(val) => handleSubSelectChange(opt.value, val)}
                                      styles={{
                                        control: (base) => ({
                                          ...base,
                                          backgroundColor: "var(--input-bg-color)",
                                          color: "var(--input-text-color)",
                                          borderColor: "#888",
                                        }),
                                        input: (base) => ({
                                          ...base,
                                          color: "var(--input-text-color)",
                                        }),
                                        menu: (base) => ({
                                          ...base,
                                          backgroundColor: "var(--input-bg-color)",
                                          color: "var(--input-text-color)",
                                        }),
                                        multiValue: (base) => ({
                                          ...base,
                                          backgroundColor: "var(--green-button-default-color)",
                                          color: "#fff",
                                        }),
                                        multiValueLabel: (base) => ({
                                          ...base,
                                          color: "#fff",
                                        }),
                                        multiValueRemove: (base) => ({
                                          ...base,
                                          color: "#fff",
                                          ":hover": { backgroundColor: "#2c974b", color: "#fff" },
                                        }),
                                      }}
                                    />
                                  </div>
                                )}
                                </div>
                              );
          })}
              </div>
              <div style={{display: "flex", justifyContent: "flex-end", gap: "10px"}}>
                  <button className="button red-button" onClick={onCancel}>
                      Cancel
                  </button>
                  <button
                      className="button green-button"
                      onClick={handleConfirm}
                      disabled={selected.length === 0}
                  >
                      Confirm
                  </button>
              </div>
          </div>
      </div>
  );
}

export default CheckboxPopup;
